import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { Play, Pause, RotateCcw, Check, Phone } from "lucide-react";
import { AuntieAvatar, UncAvatar, OleGirlAvatar, YKAvatar, AnimatedAvatar } from "../components/Avatars";

const PERSONA_MAP = {
  auntie: { name: "Auntie™", Avatar: AuntieAvatar },
  unc: { name: "Unc™", Avatar: UncAvatar },
  olegirl: { name: "Ole Girl™", Avatar: OleGirlAvatar },
  yk: { name: "YK™", Avatar: YKAvatar },
};

export default function PlaybackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const audioRef = useRef(null);

  const { 
    sessionId, 
    response, 
    persona = "auntie", 
    turnsRemaining = 0,
    crisisDetected = false 
  } = location.state || {};

  const personaData = PERSONA_MAP[persona] || PERSONA_MAP.auntie;
  const personaName = personaData.name;
  const PersonaAvatarComponent = personaData.Avatar;

  useEffect(() => {
    // Auto-play on mount if we have audio
    if (response?.audio_base64 && !crisisDetected) {
      playAudio();
    }
  }, []);

  const playAudio = () => {
    if (!response?.audio_base64) return;
    
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    const audio = new Audio(`data:audio/mp3;base64,${response.audio_base64}`);
    audioRef.current = audio;
    
    audio.onplay = () => setIsPlaying(true);
    audio.onended = () => {
      setIsPlaying(false);
      setHasPlayed(true);
    };
    audio.onpause = () => setIsPlaying(false);
    
    audio.play().catch(console.error);
  };

  const togglePlayback = () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
    } else {
      playAudio();
    }
  };

  const handleDone = async () => {
    // Clean up audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    // Refresh user data (to update vent count)
    await refreshUser();
    
    navigate("/persona");
  };

  const handleContinue = () => {
    // Go back to recorder for another turn
    navigate("/vent", { state: { continueSession: true, sessionId } });
  };

  // Crisis screen
  if (crisisDetected || response?.crisis_detected) {
    return (
      <div className="min-h-screen flex flex-col p-6 md:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-1 flex flex-col items-center justify-center text-center"
        >
          <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center mb-6">
            <Phone className="w-10 h-10 text-destructive" />
          </div>
          
          <h1 className="font-serif text-2xl font-bold mb-4">
            I Hear You
          </h1>
          
          <p className="text-muted-foreground mb-8 max-w-sm">
            What you're feeling is real. But I'm not equipped to help with this. 
            Please reach out to someone who can.
          </p>
          
          <a
            href="tel:988"
            className="bg-primary text-primary-foreground px-8 py-4 rounded-full text-lg font-semibold hover:bg-primary/90 transition-colors mb-4"
            data-testid="call-988-btn"
          >
            Call or Text 988
          </a>
          
          <p className="text-sm text-muted-foreground mb-8">
            Suicide & Crisis Lifeline — Free, 24/7
          </p>
          
          <Button
            onClick={handleDone}
            variant="ghost"
            className="text-muted-foreground"
            data-testid="done-btn"
          >
            Return Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col p-6 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 flex flex-col"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-muted-foreground text-sm mb-2">
            {personaName} says...
          </p>
        </div>

        {/* Persona Avatar */}
        <div className="flex justify-center mb-8">
          <AnimatedAvatar isActive={isPlaying}>
            <PersonaAvatarComponent size="lg" />
          </AnimatedAvatar>
        </div>

        {/* Response Text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex-1"
        >
          <div className="bg-card/50 backdrop-blur-sm rounded-3xl p-6 border border-border/50">
            <p className="text-lg leading-relaxed text-foreground font-serif">
              "{response?.text || "..."}"
            </p>
          </div>
        </motion.div>

        {/* Playback Controls */}
        <div className="flex flex-col items-center gap-6 py-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={togglePlayback}
              size="lg"
              className="w-16 h-16 rounded-full bg-primary hover:bg-primary/90"
              data-testid="play-pause-btn"
            >
              {isPlaying ? (
                <Pause className="w-6 h-6" fill="currentColor" />
              ) : (
                <Play className="w-6 h-6" fill="currentColor" />
              )}
            </Button>
            
            <Button
              onClick={playAudio}
              variant="ghost"
              size="icon"
              className="w-12 h-12 rounded-full"
              data-testid="replay-btn"
            >
              <RotateCcw className="w-5 h-5" />
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground">
            {isPlaying ? "Playing..." : hasPlayed ? "Tap to replay" : "Tap to play"}
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-3 pb-4">
          {turnsRemaining > 0 && !response?.session_complete && (
            <Button
              onClick={handleContinue}
              variant="outline"
              className="w-full h-14 rounded-full text-lg border-primary/50 hover:bg-primary/10"
              data-testid="continue-btn"
            >
              Continue Venting ({turnsRemaining} turns left)
            </Button>
          )}
          
          <Button
            onClick={handleDone}
            className="w-full h-14 rounded-full text-lg bg-primary hover:bg-primary/90"
            data-testid="done-btn"
          >
            <Check className="w-5 h-5 mr-2" />
            Done
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
